// UI/navigation helpers derived from the canonical schema:
// menu groupings, prefixes, and iterative filters for role/chain pickers.
import { CHAIN_NAMES, ROLE_NAMES } from './mapping';

export const FINGER_ROLE_PREFIXES = [
  'LeftThumb', 'LeftIndex', 'LeftMiddle', 'LeftRing', 'LeftPinky',
  'RightThumb', 'RightIndex', 'RightMiddle', 'RightRing', 'RightPinky',
] as const;

export const ARM_ROLE_PREFIXES = [
  'LeftClavicle', 'RightClavicle',
  'LeftUpperArm', 'RightUpperArm',
  'LeftLowerArm', 'RightLowerArm',
  'LeftHand', 'RightHand',
  'LeftClavicle_2', 'RightClavicle_2',
  'LeftUpperArm_2', 'RightUpperArm_2',
  'LeftLowerArm_2', 'RightLowerArm_2',
  'LeftHand_2', 'RightHand_2',
  'LeftClavicle_3', 'RightClavicle_3',
  'LeftUpperArm_3', 'RightUpperArm_3',
  'LeftLowerArm_3', 'RightLowerArm_3',
  'LeftHand_3', 'RightHand_3',
  'LeftClavicle_4', 'RightClavicle_4',
  'LeftUpperArm_4', 'RightUpperArm_4',
  'LeftLowerArm_4', 'RightLowerArm_4',
  'LeftHand_4', 'RightHand_4',
  'LeftClavicle_5', 'RightClavicle_5',
  'LeftUpperArm_5', 'RightUpperArm_5',
  'LeftLowerArm_5', 'RightLowerArm_5',
  'LeftHand_5', 'RightHand_5',
] as const;

export const LEG_ROLE_PREFIXES = [
  'LeftUpperLeg', 'RightUpperLeg',
  'LeftLowerLeg', 'RightLowerLeg',
  'LeftHock', 'RightHock',
  'LeftFoot', 'RightFoot',
  'LeftToe', 'RightToe',
] as const;

const ITERATIVE_ROLE_PATTERN = /_\d+$/;

function hasRolePrefix(roleName: string, prefixes: readonly string[]): boolean {
  return prefixes.some((prefix) => roleName.startsWith(prefix));
}

export function isIterativeRole(roleName: string): boolean {
  return ITERATIVE_ROLE_PATTERN.test(roleName);
}

const CORE_ROLE_NAMES = ROLE_NAMES.filter(
  (roleName) =>
    !hasRolePrefix(roleName, ARM_ROLE_PREFIXES) &&
    !hasRolePrefix(roleName, LEG_ROLE_PREFIXES) &&
    !hasRolePrefix(roleName, FINGER_ROLE_PREFIXES),
);

const ARM_GROUP_ROLE_NAMES = ROLE_NAMES.filter((roleName) =>
  hasRolePrefix(roleName, ARM_ROLE_PREFIXES),
);

const LEG_GROUP_ROLE_NAMES = ROLE_NAMES.filter((roleName) =>
  hasRolePrefix(roleName, LEG_ROLE_PREFIXES),
);

const FINGER_GROUP_ROLE_NAMES = ROLE_NAMES.filter((roleName) =>
  hasRolePrefix(roleName, FINGER_ROLE_PREFIXES),
);

export const ROLE_MENU_GROUPS = [
  { key: 'core', label: 'Core', roles: CORE_ROLE_NAMES },
  { key: 'arms', label: 'Arms', roles: ARM_GROUP_ROLE_NAMES },
  { key: 'legs', label: 'Legs', roles: LEG_GROUP_ROLE_NAMES },
  { key: 'fingers', label: 'Fingers', roles: FINGER_GROUP_ROLE_NAMES },
] as const;

export const FINGER_CHAIN_PREFIXES = [
  'LeftThumb', 'LeftIndex', 'LeftMiddle', 'LeftRing', 'LeftPinky',
  'RightThumb', 'RightIndex', 'RightMiddle', 'RightRing', 'RightPinky',
] as const;

export const ARM_CHAIN_PREFIXES = [
  'LeftArm', 'RightArm', 'LeftClavicle', 'RightClavicle',
  'LeftArm_2', 'RightArm_2', 'LeftClavicle_2', 'RightClavicle_2',
  'LeftArm_3', 'RightArm_3', 'LeftClavicle_3', 'RightClavicle_3',
  'LeftArm_4', 'RightArm_4', 'LeftClavicle_4', 'RightClavicle_4',
  'LeftArm_5', 'RightArm_5', 'LeftClavicle_5', 'RightClavicle_5',
] as const;

export const LEG_CHAIN_PREFIXES = [
  'LeftLeg', 'RightLeg',
  'LeftHock', 'RightHock',
  'LeftFoot', 'RightFoot',
  'LeftToe', 'RightToe',
] as const;

export const APPENDAGE_CHAIN_PREFIXES = [
  'Tail', 'Tentacle', 'Antennae',
] as const;

const ITERATIVE_CHAIN_PATTERN = /_\d+$/;

function hasChainPrefix(chainName: string, prefixes: readonly string[]): boolean {
  return prefixes.some((prefix) => chainName.startsWith(prefix));
}

export function isIterativeChain(chainName: string): boolean {
  return ITERATIVE_CHAIN_PATTERN.test(chainName);
}

export interface ChainMenuGroup {
  key: string;
  label: string;
  chains: string[];
}

const CORE_CHAIN_NAMES = CHAIN_NAMES.filter(
  (chainName) =>
    chainName === 'Root' ||
    chainName === 'Pelvis' ||
    chainName === 'Spine' ||
    chainName === 'Neck' ||
    chainName === 'Head',
);

const ARM_CHAIN_GROUP_NAMES = CHAIN_NAMES.filter((chainName) =>
  hasChainPrefix(chainName, ARM_CHAIN_PREFIXES),
);

const LEG_CHAIN_GROUP_NAMES = CHAIN_NAMES.filter((chainName) =>
  hasChainPrefix(chainName, LEG_CHAIN_PREFIXES),
);

const FINGER_CHAIN_GROUP_NAMES = CHAIN_NAMES.filter((chainName) =>
  hasChainPrefix(chainName, FINGER_CHAIN_PREFIXES),
);

const APPENDAGE_CHAIN_GROUP_NAMES = CHAIN_NAMES.filter((chainName) =>
  hasChainPrefix(chainName, APPENDAGE_CHAIN_PREFIXES),
);

const CLAIMED_CHAIN_NAMES = new Set<string>([
  ...CORE_CHAIN_NAMES,
  ...ARM_CHAIN_GROUP_NAMES,
  ...LEG_CHAIN_GROUP_NAMES,
  ...FINGER_CHAIN_GROUP_NAMES,
  ...APPENDAGE_CHAIN_GROUP_NAMES,
]);

const OTHER_DEFINED_CHAIN_NAMES = CHAIN_NAMES.filter(
  (chainName) => !CLAIMED_CHAIN_NAMES.has(chainName),
);

export const CHAIN_MENU_GROUPS = [
  { key: 'core', label: 'Core', chains: CORE_CHAIN_NAMES },
  { key: 'arms', label: 'Arms', chains: ARM_CHAIN_GROUP_NAMES },
  { key: 'legs', label: 'Legs', chains: LEG_CHAIN_GROUP_NAMES },
  { key: 'fingers', label: 'Fingers', chains: FINGER_CHAIN_GROUP_NAMES },
  { key: 'appendages', label: 'Appendages', chains: APPENDAGE_CHAIN_GROUP_NAMES },
  { key: 'other', label: 'Other', chains: OTHER_DEFINED_CHAIN_NAMES },
] as const;

const BUILT_IN_CHAIN_NAMES = new Set<string>(CHAIN_NAMES);
const BUILT_IN_CHAIN_GROUP_KEY_BY_CHAIN = new Map<string, string>(
  CHAIN_MENU_GROUPS.flatMap((group) =>
    group.chains.map((chainName) => [chainName, group.key] as const),
  ),
);

interface MutableChainMenuGroup {
  key: string;
  label: string;
  chains: string[];
  seen: Set<string>;
}

function createMutableChainGroups(): Map<string, MutableChainMenuGroup> {
  return new Map(
    CHAIN_MENU_GROUPS.map((group) => [
      group.key,
      {
        key: group.key,
        label: group.label,
        chains: [],
        seen: new Set<string>(),
      },
    ]),
  );
}

function finalizeChainGroups(
  groupsByKey: Map<string, MutableChainMenuGroup>,
): ChainMenuGroup[] {
  return CHAIN_MENU_GROUPS.map((group) => groupsByKey.get(group.key))
    .filter((group): group is NonNullable<typeof group> => Boolean(group))
    .map((group) => ({
      key: group.key,
      label: group.label,
      chains: group.chains.sort((a, b) => a.localeCompare(b)),
    }))
    .filter((group) => group.chains.length > 0);
}

function addChainToGroup(
  groupsByKey: Map<string, MutableChainMenuGroup>,
  chainName: string,
) {
  const targetGroupKey =
    BUILT_IN_CHAIN_GROUP_KEY_BY_CHAIN.get(chainName) ??
    resolveDynamicChainGroupKey(chainName);
  const targetGroup = groupsByKey.get(targetGroupKey) ?? groupsByKey.get('other');
  if (!targetGroup || targetGroup.seen.has(chainName)) return;
  targetGroup.chains.push(chainName);
  targetGroup.seen.add(chainName);
}

export function resolveDynamicChainGroupKey(chainName: string): string {
  const lower = chainName.toLowerCase();
  if (
    chainName === 'Root' ||
    chainName === 'Pelvis' ||
    chainName === 'Spine' ||
    chainName === 'Neck' ||
    chainName === 'Head'
  ) {
    return 'core';
  }
  if (lower.includes('arm') || lower.includes('clavicle')) {
    return 'arms';
  }
  if (
    lower.includes('leg') ||
    lower.includes('toe') ||
    lower.includes('foot') ||
    lower.includes('hock')
  ) {
    return 'legs';
  }
  if (
    lower.includes('thumb') ||
    lower.includes('index') ||
    lower.includes('middle') ||
    lower.includes('ring') ||
    lower.includes('pinky') ||
    lower.includes('metacarpal')
  ) {
    return 'fingers';
  }
  if (
    lower.includes('tail') ||
    lower.includes('tentacle') ||
    lower.includes('antennae') ||
    lower.includes('antenna')
  ) {
    return 'appendages';
  }
  return 'other';
}

export function buildVisibleChainGroups(
  presentChainNames: ReadonlySet<string>,
  options?: {
    supportedChainNames?: ReadonlySet<string> | null;
  },
): ChainMenuGroup[] {
  const supportedChainNames = options?.supportedChainNames ?? null;
  const groupsByKey = createMutableChainGroups();

  for (const group of CHAIN_MENU_GROUPS) {
    const targetGroup = groupsByKey.get(group.key);
    if (!targetGroup) continue;
    for (const chain of group.chains) {
      if (supportedChainNames && !supportedChainNames.has(chain)) continue;
      if (isIterativeChain(chain) && !presentChainNames.has(chain)) continue;
      if (targetGroup.seen.has(chain)) continue;
      targetGroup.chains.push(chain);
      targetGroup.seen.add(chain);
    }
  }

  for (const chain of presentChainNames) {
    if (supportedChainNames && !supportedChainNames.has(chain)) continue;
    if (BUILT_IN_CHAIN_NAMES.has(chain)) continue;
    addChainToGroup(groupsByKey, chain);
  }

  return finalizeChainGroups(groupsByKey);
}
